import {
	clearPinSetupPending,
	clearPinUnlockPending,
	isPinSessionUnlockEligible,
	isPinUnlockPending,
	readPinGateFromUser,
	setPinUnlockPending,
} from '../../../util/pinBrowserFlags'
import {
	getMsUntilLoginSessionExpiry,
	isLoginSessionWindowValid,
} from '../../../util/loginSessionWindow'
import { useCallback, useContext, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { AuthHandlerContext } from '../../../App'
import { REFRESH_TOKEN as REFRESH_TOKEN_KEY } from '../../../assets/constants'
import ROUTES from '../../../assets/routes'
import { refreshAccessTokenWithRetries } from '../../../services/auth/tokenRefresh'
import { getUserProfile } from '../../../services/apis/user'

export type PostLoginPinGate = 'setup' | 'ready'

/** Sync PIN flag from `/user/me` after auth tokens are persisted. */
export async function preparePostLoginPinGate(): Promise<PostLoginPinGate> {
	const user = await getUserProfile()
	return applyPinSetupGateFromUser(user)
}

/**
 * After credentials: unlock only when PIN is set AND mobile is verified (matches server pinSession).
 * Otherwise enter app; MobileVerificationAlert / PinSetupAlert handle onboarding.
 */
export function applyPinSetupGateFromUser(user: {
	isPinEnabled?: boolean
	is_pin_enabled?: boolean
	mobileVerified?: boolean
	mobile_verified?: boolean
}): PostLoginPinGate {
	clearPinSetupPending()
	const { unlockEligible } = readPinGateFromUser(user)
	if (unlockEligible) {
		if (!isLoginSessionWindowValid()) {
			setPinUnlockPending()
		} else {
			clearPinUnlockPending()
		}
		return 'ready'
	}
	// Incomplete onboarding (no PIN and/or mobile): never keep unlock gate — onboarding JWTs
	// block /auth/verify-pin and surface "PIN unlock or onboarding required".
	clearPinUnlockPending()
	return 'setup'
}

/**
 * Enforces the daily 6am IST session window: PIN unlock when enabled, otherwise full logout.
 */
export default function PinSessionUnlockGate({ children }: { children: React.ReactNode }) {
	const navigate = useNavigate()
	const { onLoggedOut } = useContext(AuthHandlerContext)

	const navigatingRef = useRef(false)
	const onLoggedOutRef = useRef(onLoggedOut)
	onLoggedOutRef.current = onLoggedOut

	const evaluate = useCallback(async () => {
		const hasRefresh = !!localStorage.getItem(REFRESH_TOKEN_KEY)
		if (!hasRefresh) {
			return
		}

		if (isPinUnlockPending()) {
			navigate(ROUTES.login, { replace: true })
			return
		}

		// Mobile/PIN onboarding: daily session window is not opened yet — stay in-app.
		// Do not refresh/redirect here or OTP dialogs remount and lose input.
		if (!isPinSessionUnlockEligible()) {
			return
		}

		if (isLoginSessionWindowValid()) {
			return
		}

		if (navigatingRef.current) return

		const refreshResult = await refreshAccessTokenWithRetries(3)
		// Only wipe the device session when the server rejected the refresh token.
		// Transient failures still go to PIN (submit retries / shows network error).
		if (refreshResult.status === 'unauthorized' || refreshResult.status === 'no_token') {
			clearPinUnlockPending()
			onLoggedOutRef.current(true)
			navigate(ROUTES.login, { replace: true })
			return
		}

		navigatingRef.current = true
		try {
			setPinUnlockPending()
			navigate(ROUTES.login, { replace: true })
		} finally {
			navigatingRef.current = false
		}
	}, [navigate])

	useEffect(() => {
		void evaluate()
	}, [evaluate])

	useEffect(() => {
		const onVisibility = () => {
			if (document.visibilityState !== 'visible') return
			void evaluate()
		}
		document.addEventListener('visibilitychange', onVisibility)
		return () => document.removeEventListener('visibilitychange', onVisibility)
	}, [evaluate])

	useEffect(() => {
		if (!localStorage.getItem(REFRESH_TOKEN_KEY)) return
		// No daily session timer until unlock-eligible (mobile + PIN) and window is set.
		if (!isPinSessionUnlockEligible()) return

		const ms = getMsUntilLoginSessionExpiry()
		if (ms == null) {
			void evaluate()
			return
		}

		const timeoutId = setTimeout(() => {
			void evaluate()
		}, ms + 50)

		return () => clearTimeout(timeoutId)
	}, [evaluate])

	return <>{children}</>
}
